import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import type { Asset } from '@/types';

interface PortCellProps {
  asset: Asset;
  maxVisible?: number;
  wrapInTooltip?: boolean;
}

/**
 * PortCell - Displays port numbers with "+N" counter and tooltip
 *
 * With PR #3413, port data comes from structured Port models via HAS_PORT relationships.
 *
 * Display modes:
 * - Table mode (maxVisible=1): Shows first port with "+N" counter
 * - Full display (maxVisible=Infinity): Shows all ports inline (default, backwards compatible)
 *
 * Features:
 * - Hover anywhere in cell to see all ports in tooltip (when wrapInTooltip=true)
 * - Ports sorted numerically
 * - Matches AttackSurfaceComponent pattern
 *
 * @param asset - The asset to render ports for
 * @param maxVisible - Maximum number of chips to display before "+N" counter (default: Infinity)
 * @param wrapInTooltip - Whether to wrap entire cell in tooltip (default: false)
 * @returns Port chip display or dash if not available
 */
export const PortCell: React.FC<PortCellProps> = React.memo(
  ({ asset, maxVisible = Infinity, wrapInTooltip = false }) => {
    const portNumbers = useMemo(() => {
      // Port data now comes from structured Port models
      if (
        'ports' in asset &&
        Array.isArray(asset.ports) &&
        asset.ports.length > 0
      ) {
        return asset.ports
          .map(port => port.port)
          .filter(Boolean)
          .sort((a, b) => a - b)
          .map(String);
      }

      // Fallback for backwards compatibility with old Attribute model
      if ('attributes' in asset && Array.isArray(asset.attributes)) {
        const portAttr = asset.attributes.find(attr => attr.name === 'port');
        if (portAttr?.value) {
          return [String(portAttr.value)];
        }
      }

      return [];
    }, [asset]);

    if (portNumbers.length === 0) {
      return <>-</>;
    }

    return (
      <ChipsList
        items={portNumbers}
        maxVisible={maxVisible}
        size="xs"
        wrapInTooltip={wrapInTooltip}
        data-testid="port-chips"
      />
    );
  }
);

PortCell.displayName = 'PortCell';
